import React from 'react';
import { tFunctionType } from 'app/components/localization';
import { CharacterType, PasswordType, USER } from 'app/constants/enums/PasswordType';
import { Set } from 'immutable';

const getCharacterTypesError = (t: tFunctionType, characterTypes: Set<CharacterType>) => {
  if (characterTypes.equals(Set([CharacterType.Upper]))) {
    return t('auth.password.required-upper');
  } else if (characterTypes.equals(Set([CharacterType.Lower]))) {
    return t('auth.password.required-lower');
  } else if (characterTypes.equals(Set([CharacterType.Digit, CharacterType.Special]))) {
    return t('auth.password.required-digit-or-special');
  } else {
    throw new Error(
      `Password rule contains unknown character type set: ${JSON.stringify(characterTypes)}`,
    );
  }
};

export function getPasswordValidationErrors(t: tFunctionType, password: string, rules: PasswordType) {
  if (password.length < rules.minLength) {
    return t('auth.password.invalid-length', { minLength: rules.minLength });
  }
  // Must fulfill every requirement
  const errors: string[] = [];
  rules.required.forEach((requiredSet) => {
    // For a requiredSet, must fulfill at least one of the requirements.
    let fulfilledOne = false;
    for (const req of requiredSet) {
      switch (req) {
        case CharacterType.Upper:
          if (password.match(/[A-Z]/)) {
            fulfilledOne = true;
          }
          break;
        case CharacterType.Lower:
          if (password.match(/[a-z]/)) {
            fulfilledOne = true;
          }
          break;
        case CharacterType.Digit:
          if (password.match(/[0-9]/)) {
            fulfilledOne = true;
          }
          break;
        case CharacterType.Special:
          if (password.match(/[-~!@#$%^&*_+=`|(){}[\]:;"'<>,.? ]/)) {
            fulfilledOne = true;
          }
          break;
      }
      if (fulfilledOne) break;
    }
    if (!fulfilledOne) {
      errors.push(getCharacterTypesError(t, requiredSet));
    }
  });
  if (errors.length > 0) {
    return (errors.map((error, index) =>
      <React.Fragment key={index}>
        {(index > 0) && <br />}
        {error}
      </React.Fragment>)
    );
  }
  return undefined;
}

// The default context value should be the lowest common denominator, because it will be used in
// cases where the true password type is unknown.
export const PasswordRulesContext = React.createContext<PasswordType>(USER);
